import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { APP_NAMESPACE } from '@/meta'
// Le maillon vers le BFF. Import à sens UNIQUE : `identity/preuveSlot` ne
// connaît que `identity/runtime`, qui n'importe rien d'`api/` — sans quoi le
// cycle client → preuve → client casserait le chargement du bundle.
import { invaliderPreuve, preuveCourante } from '@/identity/preuveSlot'

/**
 * Client HTTP unique de l'application.
 *
 * Toutes les fonctions de `src/api/*` passent par ici — aucune vue n'appelle
 * `axios` ni `fetch` directement. Trois choses vivent à cet endroit et nulle
 * part ailleurs :
 *
 *   1. la session persistée (jeton d'accès + jeton de rafraîchissement) ;
 *   2. le rafraîchissement automatique sur 401, sérialisé (voir plus bas) ;
 *   3. la normalisation des erreurs en `ApiError`, pour que les vues n'aient
 *      jamais à connaître la forme d'une erreur axios ;
 *   4. le mode de transport choisi à la COMPILATION (`__BTOOL_AUTH_MODE__`,
 *      BBG-215 / RFC §24) : `bearer` = les trois points ci-dessus, à
 *      l'identique d'ADR-006 ; `trousseau` = aucun des trois pour la session —
 *      le navigateur ne détient plus de porteur, c'est le cookie scellé du BFF.
 */

/**
 * Bascule de compilation, substituée au texte par le `define` de Vite/Vitest.
 * Une CONSTANTE — jamais `process.env` relu à l'exécution, jamais de repli :
 * dans un bundle `trousseau`, le chemin `bearer` ci-dessous doit être du code
 * MORT éliminé par le minificateur, pas une option désactivée. Le mode n'est
 * pas une autorité : il dit comment la session voyage, jamais ce qu'elle
 * autorise — le serveur reste seul juge.
 */
// Exportée pour le canal temps réel, qui doit distinguer « faire tourner le
// jeton » de « racheter une preuve » sans redériver la bascule de son côté :
// deux dérivations du même drapeau sont deux endroits où se tromper le jour où
// un troisième mode existera. La constante reste substituée au texte, donc le
// code mort est éliminé dans les DEUX fichiers.
export const EST_TROUSSEAU = __BTOOL_AUTH_MODE__ === 'trousseau'

// -----------------------------------------------------------------------------
// Session persistée
// -----------------------------------------------------------------------------

/** Forme de l'utilisateur renvoyée par `POST /auth/identity/login` et `GET /auth/me`. */
export interface SessionUser {
  id: string
  email: string
  /**
   * Forme d'AFFICHAGE, composée par la base à partir des deux parties.
   * Toujours présente — y compris pour un compte antérieur à la migration 0079,
   * qui n'a que celle-ci.
   */
  fullName: string
  /**
   * Prénom et nom, rendus par `/auth/me` depuis l'IDENTITÉ (jamais découpés
   * depuis `fullName` : « Anne-Marie Le Roy du Plessis » n'a pas de point de
   * coupure calculable).
   *
   * Optionnels et possiblement VIDES : la réponse de connexion ne les porte
   * pas — elle ouvre une session, elle ne décrit pas une personne — et un
   * compte dont l'identité n'a pas de profil rempli n'a pas de prénom connu.
   * Un écran qui les affiche doit donc prévoir le cas, et se rabattre sur
   * `fullName`.
   */
  firstName?: string
  lastName?: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  organisationId: string
  /** Nom de l'organisation, affiché par la barre latérale sur chaque écran. */
  organisationName: string
  locale: string
  /**
   * L'organisation de l'utilisateur est-elle l'organisation de GESTION ?
   *
   * Sert à AFFICHER la bascule de périmètre. Ce n'est pas une autorisation : le
   * serveur revérifie le droit sur chaque route du périmètre plateforme (voir
   * `plateforme/superuser/routes.ts`). Une valeur bricolée dans le stockage local ne
   * donne donc accès à rien — elle fait juste apparaître un menu qui répondra
   * 403.
   */
  isHyperadmin: boolean
}

export interface Session {
  /**
   * Preuve d'accès de la session — ABSENTE en mode trousseau.
   *
   * Le champ n'est pas optionnel « au cas où » : il dit une vérité du modèle.
   * En mode `bearer`, la page détient le porteur et le présente elle-même. En
   * mode `trousseau`, elle ne détient RIEN : le porteur durable est scellé dans
   * le cookie du BFF, et chaque requête est signée par une preuve courte
   * rachetée à la demande (`identity/preuveSlot`). Une session sans jeton n'est
   * donc pas une session incomplète, c'est la forme NORMALE du mode trousseau —
   * et la déclarer ainsi force chaque lecteur à traiter le cas au lieu de
   * transporter une chaîne vide qui mentirait.
   */
  accessToken?: string
  /**
   * Jeton de rafraîchissement — ABSENT en mode trousseau, et DÉPRÉCIÉ (RFC §22
   * DEPRECATE) : le porteur durable ne doit plus jamais descendre dans le
   * navigateur. Il subsiste pour le mode `bearer`, le temps de la migration.
   */
  refreshToken?: string
  user: SessionUser
}

// Namespace pointé : toutes les clés de stockage du projet commencent par
// `APP_NAMESPACE.` (voir src/meta.ts). Sans convention, on retrouve six clés
// orphelines en deux ans, et on n'ose plus en supprimer aucune.
const STORAGE_KEY = `${APP_NAMESPACE}.session`

/**
 * Valide une valeur brute du stockage de session.
 *
 * Le même contrôle sert au démarrage (`loadSession`) et à la synchronisation
 * multi-onglets ci-dessous : une écriture illisible doit être refusée au même
 * titre qu'un stockage corrompu, pas par une règle différente qui divergerait.
 */
function parseSession(raw: string | null): Session | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<Session>
    if (!parsed?.accessToken || !parsed?.refreshToken || !parsed?.user) return null
    return parsed as Session
  } catch {
    return null
  }
}

/**
 * Lit la session persistée. Retourne `null` sur toute anomalie (stockage
 * indisponible, JSON corrompu, forme inattendue) : une session illisible doit
 * déconnecter proprement, jamais faire planter le démarrage de l'application.
 */
export function loadSession(): Session | null {
  // Mode trousseau : le navigateur ne détient PLUS AUCUN porteur de session —
  // la fonction reste (des consommateurs l'importent) mais il n'y a rien à
  // relire. Une clé résiduelle laissée par une ancienne version bearer n'est
  // PAS adoptée ici : l'import des sessions existantes est un acte serveur en
  // une seule tentative (E6-T2), jamais un effet de bord du chargement.
  if (EST_TROUSSEAU) return null
  try {
    return parseSession(localStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

export function saveSession(session: Session): void {
  if (!EST_TROUSSEAU) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    } catch {
      // Navigation privée ou quota plein : la session reste en mémoire pour cet
      // onglet. Dégradation acceptable — on ne bloque pas la connexion.
    }
  }
  current = session
}

/**
 * Remplace l'UTILISATEUR de la session persistée, en gardant les jetons.
 *
 * Nécessaire parce que deux lecteurs coexistent : le store Pinia (hydraté au
 * montage) et le garde du routeur, qui lit la session persistée — il s'exécute
 * avant le premier montage de composant, quand le store n'existe pas encore.
 *
 * Sans cette synchronisation, les deux divergent dès qu'un champ apparaît dans
 * la charge de `/auth/me` : le store voit le nouveau champ, la session non, et
 * l'interface affiche un item de menu que le garde refuse. Constaté exactement
 * ainsi avec `isHyperadmin` — le clic sur « Organisations » ne faisait « rien »,
 * parce que le garde redirigeait silencieusement vers l'écran déjà affiché.
 */
export function updateSessionUser(user: SessionUser): void {
  if (current === null) return
  saveSession({ ...current, user })
}

/**
 * L'ÉCOSYSTÈME du point d'entrée courant, et le rejet d'une session étrangère
 * (BBG-149 §11.1.2).
 *
 * ─── Ce qui protège déjà, et pourquoi ça ne suffit pas ──────────────────────
 *
 * Chaque tenant a son propre hôte (BBG-73), donc sa propre ORIGINE, donc son
 * propre `localStorage` : deux onglets ouverts sur deux écosystèmes ne
 * partagent rien, et l'événement `storage` ne franchit pas non plus la
 * frontière d'origine. La coexistence exigée par le §11.1 est donc acquise par
 * construction — le navigateur la fait respecter, pas nous.
 *
 * Mais elle est acquise INCIDEMMENT : rien, dans ce code, ne DIT que la session
 * gardée appartient à l'écosystème de la page ouverte. Le jour où une origine
 * servirait deux tenants, ou si un hôte changeait de main, une session périmée
 * paraîtrait valide jusqu'à la première requête. Le serveur la refuserait — le
 * `tenant_id` signé dans la preuve ne correspondrait pas — mais l'utilisateur
 * verrait « déconnecté sans raison », le pire des symptômes.
 *
 * Cette fonction transforme donc « isolé parce que les origines diffèrent » en
 * « isolé et vérifié ».
 *
 * ─── Pourquoi elle ne déconnecte PAS en cas de panne réseau ─────────────────
 *
 * Ne pas pouvoir joindre le point d'entrée n'apprend rien sur la session. La
 * déconnexion sur échec réseau transformerait une coupure passagère en perte de
 * session pour tout le monde — et le serveur reste l'autorité : une session
 * réellement étrangère est refusée à la première requête. On n'agit donc que
 * sur une réponse REÇUE et contradictoire.
 */
export function rejeterSessionEtrangere(tenantDuPointDEntree: string): boolean {
  const session = currentSession()
  if (session === null) return false
  if (session.user.organisationId === tenantDuPointDEntree) return false
  clearSession()
  return true
}

export function clearSession(): void {
  if (!EST_TROUSSEAU) {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Sans importance : `current = null` suffit à couper l'accès.
    }
  }
  current = null
}

// -----------------------------------------------------------------------------
// Import unique de la session historique (BBG-216 / E6-T2, RFC §24 PHASE 3)
// -----------------------------------------------------------------------------

/* v8 ignore start -- hors mesure bearer : la couverture est mesurée sur la
 * SEULE exécution bearer de la suite (décision E6-T1, vitest.config.ts), or ce
 * code est MORT dans cette compilation — `EST_TROUSSEAU` y est fausse, rien
 * après la garde de `amorcerImportSessionLegacy` ne peut s'y exécuter. Il est
 * éprouvé dans l'AUTRE exécution : les tests MI-01/02/03 de client.spec.ts le
 * traversent en mode trousseau, dont vitest ne fusionne pas la mesure.
 * Instrumenter du code inexécutable ici ferait mentir le plancher, avec la
 * même justification que l'exclusion des fichiers déclaratifs. */

/**
 * Cookie et en-tête du double-submit exigés par le BFF sur toute écriture
 * (RFC §18). DÉFINIS ICI, pas importés : le BFF et ce bundle sont deux
 * déploiements distincts qui ne partagent aucun module.
 */
const NOM_COOKIE_CSRF = 'betool-csrf'
const ENTETE_CSRF = 'x-betool-csrf'

/** Lit le jeton de double-submit, ou `null` s'il n'a pas encore été amorcé. */
function lireJetonCsrf(): string | null {
  if (typeof document === 'undefined') return null
  const paire = document.cookie
    .split(';')
    .map((morceau) => morceau.trim())
    .find((morceau) => morceau.startsWith(`${NOM_COOKIE_CSRF}=`))
  const valeur = paire?.slice(NOM_COOKIE_CSRF.length + 1) ?? ''
  return valeur === '' ? null : valeur
}

/**
 * Troque la session `bearer` d'avant le déploiement du trousseau contre un
 * slot, UNE SEULE FOIS, au premier chargement d'un bundle `trousseau`.
 *
 * ─── Effacement AVANT le réseau ─────────────────────────────────────────────
 *
 * La clé part avant l'appel, quoi qu'il arrive ensuite : c'est ce qui rend le
 * « une seule fois » structurel. Un rechargement pendant la requête, une
 * réponse perdue alors que le moteur a déjà consommé le jeton — dans tous les
 * cas, aucune seconde présentation ne pourra partir d'un navigateur qui n'a
 * plus rien à présenter. Le prix d'une coupure pile à cet instant est une
 * reconnexion : verdict assumé du ticket (« import échoué = reconnexion »).
 *
 * ─── Silencieux, dans tous les sens ──────────────────────────────────────────
 *
 * La promesse ne rejette JAMAIS et aucun échec n'est remonté : l'import qui
 * échoue (jeton mort, réseau, 503) laisse l'application démarrer déconnectée,
 * ce que la suite du démarrage affiche d'elle-même. Et rien n'est fait en
 * mode `bearer` : là-bas, la clé historique EST la session.
 */
export async function amorcerImportSessionLegacy(): Promise<void> {
  if (!EST_TROUSSEAU) return

  let brut: string | null = null
  try {
    brut = localStorage.getItem(STORAGE_KEY)
  } catch {
    // Stockage indisponible : on ne peut ni lire ni effacer — démarrage
    // simplement déconnecté, comme si la clé n'existait pas.
    return
  }
  if (brut === null) return

  const sessionHistorique = parseSession(brut)
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Même tolérance que `clearSession` : la valeur lue est en mémoire,
    // l'import part quand même.
  }
  // Une valeur qui n'est pas une session ne pourra JAMAIS être importée : la
  // garder la ferait relire à chaque démarrage, sans espoir de succès.
  if (sessionHistorique === null) return

  // Le double-submit exige un jeton posé PAR l'origine du BFF. En production
  // le GET du HTML l'a déjà amorcé ; ailleurs (développement derrière le
  // relais Vite), cette lecture volontaire le fait — le BFF pose le cookie sur
  // TOUTE lecture, même refusée : son 401 attendu n'a rien à interpréter.
  let csrf = lireJetonCsrf()
  if (csrf === null) {
    try {
      await fetch('/portail/trousseau', { credentials: 'same-origin' })
    } catch {
      return
    }
    csrf = lireJetonCsrf()
    // Sans amorce, l'écriture serait refusée en 403 : inutile de la tenter,
    // la clé est déjà partie — la reconnexion suivra.
    if (csrf === null) return
  }

  // `fetch` nu volontairement : l'instance `http` est taillée pour `/api` —
  // baseURL suffixée, intercepteur de 401 qui « déconnecte ». Aucune de ces
  // conduites n'a de sens ici : l'échec d'import EST déjà une déconnexion
  // silencieuse (clé effacée ci-dessus), le déclencher serait du bruit.
  try {
    await fetch('/portail/trousseau/importer', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [ENTETE_CSRF]: csrf },
      body: JSON.stringify({ refreshToken: sessionHistorique.refreshToken }),
    })
    // 200 ou refus : rien à LIRE. Le slot rangé vit dans le cookie posé par
    // le BFF, hors de portée du JavaScript — c'est le principe du chantier.
  } catch {
    // Silencieux : voir l'en-tête de fonction.
  }
}
/* v8 ignore stop */

// Copie en mémoire, pour ne pas relire (et re-parser) le localStorage à chaque
// requête. `loadSession()` au démarrage rétablit la session d'un rechargement.
let current: Session | null = loadSession()

export function currentSession(): Session | null {
  return current
}

// -----------------------------------------------------------------------------
// Erreurs
// -----------------------------------------------------------------------------

/**
 * Erreur normalisée. Les vues attrapent `ApiError` et affichent `.message` :
 * elles n'ont jamais à distinguer un échec réseau d'une réponse 500.
 */
export class ApiError extends Error {
  /** Statut HTTP, ou 0 quand la requête n'a jamais atteint le serveur. */
  readonly status: number
  /** Code métier optionnel renvoyé par l'API (ex. `invalid_credentials`). */
  readonly code: string | undefined
  /** Détails de validation, indexés par champ, tels que renvoyés par l'API. */
  readonly fields: Record<string, string> | undefined

  constructor(
    message: string,
    status: number,
    code?: string,
    fields?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
  }

  /** Vrai quand le serveur n'a pas répondu (hors ligne, port fermé, DNS). */
  get isNetwork(): boolean {
    return this.status === 0
  }
}

/** Traduit une erreur axios en `ApiError`, avec un message affichable tel quel. */
function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      error?: string
      message?: string
      code?: string
      fields?: Record<string, string>
    }>
    const status = axiosError.response?.status ?? 0
    const body = axiosError.response?.data

    if (status === 0) {
      return new ApiError(
        'Serveur injoignable. Vérifiez que l’API est démarrée.',
        0,
        'network_error',
      )
    }
    const message =
      body?.error ??
      body?.message ??
      `La requête a échoué (HTTP ${status}).`
    return new ApiError(message, status, body?.code, body?.fields)
  }

  return new ApiError(
    error instanceof Error ? error.message : 'Erreur inattendue.',
    0,
    'unknown',
  )
}

// -----------------------------------------------------------------------------
// Instance axios
// -----------------------------------------------------------------------------

// Mode trousseau : le bundle est servi PAR l'origine d'identité du tenant — les
// requêtes partent en chemin RELATIF, même origine, et le navigateur y joint le
// cookie scellé de lui-même, sans qu'aucun code ne le touche. Il n'y a donc
// plus d'adresse d'API à configurer ici : celui qui sert la page est celui qui
// répond.
const baseURL = EST_TROUSSEAU
  ? ''
  : ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://127.0.0.1:3000')

/**
 * Adresse publique de l'API, normalisée, suffixe `/api` compris.
 *
 * Exposée pour la documentation d'intégration (`components/docs/`) : les
 * exemples copiés-collés doivent montrer le domaine RÉEL que le SPA appelle,
 * pas `window.location.origin` — en développement, l'API et le SPA ne vivent
 * pas sur la même origine.
 */
export const API_BASE_URL = `${baseURL.replace(/\/+$/, '')}/api`

export const http = axios.create({
  baseURL: `${baseURL.replace(/\/+$/, '')}/api`,
  timeout: 20_000,
  // PAS de `Content-Type` par défaut ici. Axios le pose lui-même dès qu'il y a
  // un corps à sérialiser, et l'annoncer sur TOUTE requête casse celles qui
  // n'en ont pas : un `DELETE` sans corps arrivait avec
  // `Content-Type: application/json` et Fastify le rejetait en 400
  // (FST_ERR_CTP_EMPTY_JSON_BODY). Autrement dit, aucune suppression ne
  // fonctionnait — vérifié, puis corrigé ici et rendu tolérable côté serveur.
})

/** Marque une requête déjà rejouée après rafraîchissement (anti-boucle). */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

http.interceptors.request.use((config) => {
  // En mode trousseau, AUCUNE en-tête Authorization : le porteur est le cookie
  // du trousseau, joint par le navigateur à chaque requête même origine. Poser
  // un Bearer ici recréerait exactement le secret lisible en JavaScript que le
  // chantier supprime (RFC §22 REMOVE — la ligne disparaît du bundle compilé).
  //
  // `??=` et non `=` : un appelant qui pose EXPLICITEMENT le porteur a le
  // dernier mot. C'est le cas du changement de compte (identity/adaptateurComptes)
  // — le Bearer présenté est la preuve courte du slot VISÉ, pas le jeton de la
  // session encore en place : écraser l'explicite enverrait l'émission signée
  // sous l'ANCIENNE identité, et le « switch » aurait silencieusement reconnecté
  // le compte qu'on quittait.
  if (!EST_TROUSSEAU && current?.accessToken) {
    config.headers.Authorization ??= `Bearer ${current.accessToken}`
  }
  return config
})

// LE MAILLON, côté page (mode trousseau UNIQUEMENT).
//
// Le moteur est Bearer-only et le restera (§22 KEEP : `credentials: false`,
// aucun cookie) ; le porteur durable, lui, est scellé dans le cookie du
// trousseau, hors de portée de tout script. Entre les deux, il manquait
// quelqu'un pour traduire — d'où les requêtes qui partaient nues et les 401
// immédiats après un login pourtant accepté.
//
// La traduction se fait au BFF (`POST /portail/session/preuve`, BBG-211) : il
// résout le slot de CET onglet dans le scellé de la requête, l'échange en
// serveur-à-serveur contre une preuve COURTE, et la rend à la page. C'est
// exactement le patron du kiosk — `Bearer` + `X-Betool-Slot` sur chaque appel
// (`client/src/console/api.js`) — à ceci près que le jeton de rafraîchissement
// ne descend jamais dans le navigateur.
//
// Intercepteur SÉPARÉ, et non une branche du précédent : axios les exécute
// dans l'ordre, celui-ci est asynchrone, et les garder distincts laisse le
// chemin `bearer` strictement synchrone et inchangé.
http.interceptors.request.use(async (config) => {
  if (!EST_TROUSSEAU) return config
  // L'explicite garde le dernier mot, même règle que ci-dessus : le changement
  // de compte présente la preuve du slot VISÉ, pas celle du slot courant.
  if (config.headers.Authorization !== undefined) return config

  const preuve = await preuveCourante()
  // Aucun slot actif (runtime pas encore listé, navigateur sans trousseau) :
  // on part sans en-tête et on laisse le moteur trancher. Inventer un refus
  // ici masquerait la vraie cause derrière une erreur de client.
  if (preuve !== null) config.headers.Authorization = `Bearer ${preuve}`
  return config
})

// Rafraîchissement SÉRIALISÉ. Sans cette promesse partagée, un écran qui lance
// cinq requêtes en parallèle au chargement déclenche cinq rafraîchissements
// concurrents : le backend révoque le jeton à chaque rotation, donc quatre des
// cinq échouent et déconnectent l'utilisateur. Ici, le premier 401 crée la
// promesse, les suivants l'attendent.
let refreshing: Promise<string> | null = null

/**
 * Rafraîchit le jeton d'accès, en PARTAGEANT la tentative en cours.
 *
 * Exportée parce que le client temps réel en a besoin : quand le serveur ferme
 * le canal pour jeton expiré, il faut un jeton neuf avant de se reconnecter. S'il
 * appelait son propre rafraîchissement, deux rotations concurrentes partiraient
 * — et comme le serveur révoque l'ancien jeton à chaque rotation, l'une des deux
 * échouerait et déconnecterait l'utilisateur. C'est exactement le défaut que la
 * promesse partagée évite côté HTTP ; il n'y a aucune raison d'en ouvrir une
 * seconde porte.
 */
/**
 * LE porteur à présenter, quel que soit le mode — source UNIQUE.
 *
 * Le client HTTP le pose par intercepteur ; le canal temps réel doit le
 * présenter dans son message d'authentification, et il n'a aucun moyen de
 * passer par un intercepteur. Sans cette fonction, chacun irait le chercher où
 * il croit qu'il se trouve — la session pour l'un, le BFF pour l'autre — et
 * les deux divergeraient : un canal qui survit à une session morte, ou une
 * session vivante dont le canal refuse de s'ouvrir.
 *
 * Rend `null` quand il n'y a rien à présenter : pas de session en `bearer`,
 * aucun slot actif en `trousseau`. L'appelant décide — le canal se ferme, une
 * requête part sans en-tête et laisse le serveur trancher.
 */
export async function porteurCourant(): Promise<string | null> {
  if (EST_TROUSSEAU) return preuveCourante()
  return current?.accessToken ?? null
}

export function refreshSessionOnce(): Promise<string> {
  // Mode trousseau : PLUS RIEN à rafraîchir — le secret du slot vit dans le
  // scellé, côté BFF, et ne tourne pas (ADR-014). Un appel ici est une erreur
  // de CODE, pas une session expirée : rejet immédiat et bruyant — un no-op
  // silencieux laisserait un appelant croire qu'un jeton neuf existe.
  if (EST_TROUSSEAU) {
    return Promise.reject(
      new ApiError(
        'Aucun rafraîchissement en mode trousseau : le porteur est au BFF.',
        400,
        'mode_trousseau',
      ),
    )
  }
  refreshing ??= refreshAccessToken().finally(() => {
    refreshing = null
  })
  return refreshing
}

async function refreshAccessToken(): Promise<string> {
  const session = current
  if (!session) throw new ApiError('Session absente.', 401, 'no_session')

  // Instance NUE (pas `http`) : passer par l'instance intercepteur ferait
  // repasser la réponse dans la logique de 401 et créerait une récursion.
  const response = await axios.post<{ accessToken: string; refreshToken: string }>(
    `${baseURL.replace(/\/+$/, '')}/api/auth/refresh`,
    { refreshToken: session.refreshToken },
    { timeout: 10_000, headers: { 'Content-Type': 'application/json' } },
  )

  const next: Session = {
    ...session,
    accessToken: response.data.accessToken,
    // Le backend fait tourner le jeton de rafraîchissement à chaque usage
    // (rotation) : réutiliser l'ancien serait rejeté au coup suivant.
    refreshToken: response.data.refreshToken,
  }
  saveSession(next)
  // On rend la valeur reçue du serveur, pas une relecture du champ de session :
  // celui-ci est optionnel depuis que le mode trousseau existe (une session y
  // vit sans porteur), et le relire obligerait à affirmer sa présence par une
  // assertion — qui deviendrait fausse en silence le jour où ce chemin serait
  // atteint dans l'autre mode. Ce rafraîchissement n'existe qu'en `bearer`, où
  // la réponse porte TOUJOURS le jeton : c'est cela qu'on retourne.
  return response.data.accessToken
}

// Rappel posé par main.ts après l'installation de Pinia : le client HTTP ne
// connaît ni le store ni le routeur (il serait alors impossible de le tester
// sans monter l'application entière).
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler
}

// -----------------------------------------------------------------------------
// Synchronisation multi-onglets (ADR-006, critères 4 et 5)
// -----------------------------------------------------------------------------
//
// La session vit dans un `localStorage` PARTAGÉ par tous les onglets de
// l'origine, mais la copie en mémoire `current` est propre à chaque onglet. Sans
// écouteur, deux onglets divergent dès la première rotation :
//
//   * l'onglet A rafraîchit et fait tourner le jeton de rafraîchissement ;
//   * l'onglet B garde l'ANCIEN jeton : révoqué par la rotation, il échoue et
//     déconnecte un utilisateur dont la session est parfaitement valide ;
//   * et un `logout` dans un onglet laissait les autres ouverts, avec un canal
//     temps réel qui continuait de recevoir.
//
// L'événement `storage` ne se déclenche que dans les AUTRES onglets : celui qui
// écrit n'est jamais notifié, ce code ne peut donc pas boucler avec
// `saveSession` / `clearSession`.

// Rappel posé par `main.ts` : des jetons fraîchement adoptés sont arrivés depuis
// un autre onglet. Le consommateur naturel est le client temps réel, bloqué en
// « refus » après une rotation révoquée par l'autre onglet — mais l'importer ici
// créerait un cycle (`realtime` importe `refreshSessionOnce`).
let onSessionAdopted: (() => void) | null = null

export function setSessionAdoptedHandler(handler: () => void): void {
  onSessionAdopted = handler
}

// Mode trousseau : cet écouteur n'a PLUS de raison d'être — il n'existe plus
// de clé partagée à synchroniser. Ne pas l'installer du tout (RFC §22 REMOVE),
// plutôt que de le brancher « pour rien » : un garde-fou inerte est un leurre.
if (!EST_TROUSSEAU && typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    // Onglet déjà déconnecté : rien à adopter, rien à propager. Un `login` dans
    // un autre onglet n'ouvre PAS une session ici — le store ne connaît pas cet
    // utilisateur, l'interface resterait sur l'écran de connexion par-dessus des
    // jetons actifs. Ce onglet se connectera par ses propres moyens.
    if (current === null) return

    // Valeur EFFACÉE : l'autre onglet s'est déconnecté. Le serveur a révoqué la
    // session — tenter un rafraîchissement échouerait. Même chemin qu'un 401 :
    // le gestionnaire de `main.ts` appelle `forgetLocally()`, qui ferme AUSSI le
    // canal temps réel.
    if (event.newValue === null) {
      clearSession()
      onUnauthorized?.()
      return
    }

    const adopted = parseSession(event.newValue)
    // Valeur illisible (écriture tronquée, stockage altéré) : on l'ignore et
    // l'onglet garde SA session en mémoire, encore valide côté serveur.
    // Déconnecter sur une écriture illisible punirait un onglet sain.
    if (adopted === null) return

    // FRONTIÈRE D'ÉCOSYSTÈME — défense en PROFONDEUR (BBG-149, DR-8).
    //
    // Aujourd'hui, deux écosystèmes vivent sur deux hôtes distincts, et le
    // stockage local est cloisonné par ORIGINE : une session de B ne peut pas
    // apparaître dans le stockage de A. Le serveur pose une seconde barrière —
    // un jeton présenté sur l'hôte d'un autre écosystème est refusé (401,
    // frontière d'hôte, §14 cas E).
    //
    // Ce contrôle-ci est le TROISIÈME, et il existe parce que les deux autres
    // sont des barrières d'ENVIRONNEMENT, pas de code : la première tombe le
    // jour où deux écosystèmes partagent une origine (routage par chemin,
    // sous-domaine mutualisé, configuration de préproduction), la seconde ne
    // s'exerce qu'au premier appel réseau. Entre les deux, cet onglet aurait
    // déjà ADOPTÉ l'identité d'un autre écosystème et affiché son nom
    // d'organisation.
    //
    // Le refus est SILENCIEUX et NON destructif, comme la branche « illisible »
    // juste au-dessus : l'onglet garde sa propre session, qui reste valide.
    // Se déconnecter donnerait à une écriture étrangère le pouvoir de fermer
    // une session saine — une dénégation de service à coût nul.
    if (adopted.user.organisationId !== current.user.organisationId) return

    // Pas de `saveSession` : réécrire la même valeur ne notifierait pas les
    // autres onglets, et ne ferait ici que re-parser pour rien.
    current = adopted
    onSessionAdopted?.()
  })
}

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      return Promise.reject(toApiError(error))
    }

    const config = error.config as RetriableConfig | undefined
    const isRefreshCall = config?.url?.includes('/auth/refresh')

    // Mode trousseau : la preuve courte a pu mourir AVANT son échéance annoncée
    // — révocation à distance, fermeture de famille, rotation de clé de
    // signature. Un premier 401 ne prouve donc pas que le porteur est mort : il
    // prouve que CETTE preuve ne vaut plus. On la jette, on en rachète une au
    // BFF et on rejoue UNE fois. Si le second 401 tombe, c'est le slot lui-même
    // qui est fini (le BFF aurait refusé l'échange), et la déconnexion est le
    // seul verdict honnête.
    if (EST_TROUSSEAU) {
      if (config && !config._retried) {
        invaliderPreuve()
        try {
          const preuve = await preuveCourante()
          if (preuve !== null) {
            config._retried = true
            config.headers.Authorization = `Bearer ${preuve}`
            return await http.request(config)
          }
        } catch {
          // L'échange lui-même a échoué (401 du BFF = porteur mort, ou panne) :
          // on tombe dans la déconnexion ci-dessous, sans masquer l'erreur
          // d'origine, qui est celle que l'appelant attend.
        }
      }
      clearSession()
      onUnauthorized?.()
      return Promise.reject(toApiError(error))
    }

    // Pas de session, requête déjà rejouée, ou c'est le rafraîchissement
    // lui-même qui a échoué : plus rien à tenter, on déconnecte.
    if (!config || config._retried || isRefreshCall || !current) {
      clearSession()
      onUnauthorized?.()
      return Promise.reject(toApiError(error))
    }

    try {
      // Même fonction que celle utilisée par le client temps réel : une seule
      // implémentation, donc une seule promesse partagée.
      const token = await refreshSessionOnce()
      config._retried = true
      config.headers.Authorization = `Bearer ${token}`
      return http.request(config)
    } catch {
      clearSession()
      onUnauthorized?.()
      return Promise.reject(toApiError(error))
    }
  },
)

// -----------------------------------------------------------------------------
// Enveloppes typées
// -----------------------------------------------------------------------------

// `http` renvoie une réponse axios ; ces quatre helpers renvoient directement le
// corps typé. Cela évite un `.data` — et un oubli de `.data` — à chaque appel
// dans les modules de src/api.
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const { data } = await http.get<T>(url, config)
    return data
  } catch (erreur) {
    if (!doitRejouerGet(erreur, config)) throw erreur
    // Petite pause avant la reprise : un échec réseau qui se rejoue dans la
    // même milliseconde frappe un serveur qui n'a pas eu le temps de récupérer.
    await new Promise((resoudre) => setTimeout(resoudre, 400))
    const { data } = await http.get<T>(url, config)
    return data
  }
}

/**
 * Le GET est la SEULE méthode à être rejouée automatiquement, et une seule
 * fois : il est idempotent — le refaire ne peut ni créer de doublon ni modifier
 * deux fois la même donnée. Rejouer un POST ou un DELETE dont la réponse
 * s'est perdue pourrait exécuter l'action DEUX fois ; là, il faut que l'échec
 * remonte et que l'utilisateur décide.
 *
 * Conditions, chacune pour une raison :
 *
 *   * `isNetwork` ou 5xx — fautes qui peuvent être transitoires (microcoupure,
 *     redémarrage). Un 4xx est une réponse DÉFINITIVE : la rejouer rendrait le
 *     même verdict ;
 *   * signal non annulé : l'annulation d'une requête par `useAsyncRequest`
 *     atterrit ici déguisée en erreur réseau (pas de réponse) — la rejouer
 *     partirait contre la volonté de l'appelant ;
 *   * pas de rafraîchissement : `/auth/refresh` est un POST nu hors de
 *     l'instance `http`, donc immunisé par construction.
 */
function doitRejouerGet(erreur: unknown, config?: AxiosRequestConfig): boolean {
  if (config?.signal?.aborted) return false
  if (!(erreur instanceof ApiError)) return false
  return erreur.isNetwork || erreur.status >= 500
}

export async function post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.post<T>(url, body, config)
  return data
}

export async function patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.patch<T>(url, body, config)
  return data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.delete<T>(url, config)
  return data
}
